import { Box } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';

import { PassageProps } from '../../@types/Components';
import { EInteractionMode, ESubMenu, EUnitType } from '../../@types/Enums';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useLanguage } from '../../provider/LanguageProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { DEFAULT_INDICATOR_FONT_SIZE_XS, INTERNAL_TO_CM_FACTOR, UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../utils/conversionUtils';
import { localizeNumber } from '../../utils/numberUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { snapPassageToWall } from '../../utils/wallElementSnapping';
import RoundedBoxWithText from './RoundedBoxWithText';

interface PassageLengthIndicatorProps {
  wallSideALength: number;
  rotation: number;
}

const PassageLengthIndicator = ({ wallSideALength, rotation }: PassageLengthIndicatorProps): React.JSX.Element => {
  const { locale } = useLanguage();
  const { unitType } = useUnitContext();
  const intl = useIntl();

  const meterLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'meter'), [intl, locale]);
  const footLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'foot'), [intl, locale]);

  const passageWidthText = useMemo(
    () =>
      unitType === EUnitType.METRIC
        ? localizeNumber(wallSideALength.toFixed(2), locale) + meterLocalizedLabel
        : localizeNumber(convertTo(wallSideALength, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) +
          footLocalizedLabel,
    [footLocalizedLabel, locale, meterLocalizedLabel, unitType, wallSideALength],
  );

  return (
    <group name="group-passageLengthIndicator" position={[0, 0, 0.08]} renderOrder={RENDER_ORDERS.passage + 1300}>
      <group rotation={[0, 0, -rotation]} renderOrder={RENDER_ORDERS.passage + 1300}>
        <RoundedBoxWithText boxSize={[3, 1, 0]} position={new THREE.Vector3(0, 0, 0)} fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS} textColor="black" text={passageWidthText} />
      </group>
    </group>
  );
};

export { snapPassageToWall };

const Passage = ({ wallModel, onWallUpdate, passage, onClick, selectedPassage, wallIndex, passageIndex }: PassageProps): React.JSX.Element => {
  const { length: width, angle } = passage;

  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const [passageWidth, setPassageWidth] = useState<number>(width);

  const { interactionMode, setCurrentlyActiveSubMenu } = useToolbarContext();
  const { setPassageWidthAttribute } = useBuilding();
  const { showPassageLength } = useMultiSelectToggleContext();
  const { unitType } = useUnitContext();

  const widthCm = passage.length * INTERNAL_TO_CM_FACTOR;

  const widthValue =
    unitType === EUnitType.METRIC
      ? parseFloat(widthCm.toFixed(3))
      : Math.round(convertTo(widthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));

  const widthUnit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  useEffect(() => {
    setPassageWidthAttribute(wallIndex, passageIndex, widthValue, widthUnit);
  }, [setPassageWidthAttribute, wallIndex, passageIndex, widthValue, widthUnit]);

  useEffect(() => {
    setPassageWidth(width);
  }, [width]);

  const isSelected = selectedPassage.wallIndex !== undefined && selectedPassage.passageIndex !== undefined;

  const meshColor = useMemo(() => (isSelected ? '#009fe3' : hovered ? '#888888' : 'white'), [hovered, isSelected]);

  const constraintFunction = useCallback((newPosition: THREE.Vector3): THREE.Vector3 => snapPassageToWall(wallModel, newPosition, width), [wallModel, width]);

  const updatePassagePosition = useCallback(
    (newPosition: THREE.Vector3): void => {
      passage.position = newPosition;
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.PASSAGE_POSITION_CHANGED);
    },
    [onWallUpdate, wallModel, passage],
  );

  useDraggableMesh(groupRef, constraintFunction, updatePassagePosition, interactionMode !== EInteractionMode.MOVE, interactionMode !== EInteractionMode.MOVE);

  const passageCoverSize = useMemo(() => {
    const overlap = 0.12;

    return {
      width: passageWidth + overlap,
      depth: wallModel.depth + overlap,
    };
  }, [passageWidth, wallModel.depth]);

  const passageDashSegments = useMemo(() => {
    const outlineOffset = 0.015;

    const halfWidth = passageWidth / 2 + outlineOffset;
    const halfDepth = wallModel.depth / 2 + outlineOffset;

    const dashLength = 0.38;
    const gapLength = 0.2;
    const thickness = 0.12;
    const z = 0.01;

    type DashSegment = {
      position: [number, number, number];
      rotation: number;
      size: [number, number, number];
    };

    const createDashes = (start: THREE.Vector3, end: THREE.Vector3): DashSegment[] => {
      const direction = new THREE.Vector3().subVectors(end, start);
      const length = direction.length();

      if (length <= 0) return [];

      direction.normalize();

      const lineAngle = Math.atan2(direction.y, direction.x);
      const dashes: DashSegment[] = [];

      let current = 0;

      while (current < length) {
        const currentDashLength = Math.min(dashLength, length - current);
        const centerDistance = current + currentDashLength / 2;
        const center = start.clone().addScaledVector(direction, centerDistance);

        dashes.push({
          position: [center.x, center.y, z],
          rotation: lineAngle,
          size: [currentDashLength, thickness, 0.004],
        });

        current += dashLength + gapLength;
      }

      return dashes;
    };

    return [
      ...createDashes(new THREE.Vector3(-halfWidth, halfDepth, 0), new THREE.Vector3(halfWidth, halfDepth, 0)),
      ...createDashes(new THREE.Vector3(halfWidth, halfDepth, 0), new THREE.Vector3(halfWidth, -halfDepth, 0)),
      ...createDashes(new THREE.Vector3(halfWidth, -halfDepth, 0), new THREE.Vector3(-halfWidth, -halfDepth, 0)),
      ...createDashes(new THREE.Vector3(-halfWidth, -halfDepth, 0), new THREE.Vector3(-halfWidth, halfDepth, 0)),
    ];
  }, [passageWidth, wallModel.depth]);

  useEffect(() => {
    if (!groupRef.current) return;

    groupRef.current.traverse((object) => {
      object.renderOrder = RENDER_ORDERS.passage + 1000;
      object.frustumCulled = false;

      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        const material = object.material;

        if (Array.isArray(material)) {
          material.forEach((mat) => {
            mat.depthTest = false;
            mat.depthWrite = false;
            mat.needsUpdate = true;
          });
        } else if (material) {
          material.depthTest = false;
          material.depthWrite = false;
          material.needsUpdate = true;
        }
      }
    });
  }, [passageDashSegments, hovered, isSelected]);

  const handlePointerEvents = (event: ThreeEvent<PointerEvent>, callback: (e: ThreeEvent<PointerEvent>) => void): void => {
    event.stopPropagation();
    setCurrentlyActiveSubMenu(ESubMenu.NONE);

    if (interactionMode !== EInteractionMode.MOVE) {
      callback && callback(event);
    }
  };

  return (
    <group
      name={`group-passage#${selectedPassage.passageIndex}-wall#${selectedPassage.wallIndex}`}
      ref={groupRef}
      position={passage.position}
      rotation={[0, 0, angle]}
      renderOrder={RENDER_ORDERS.passage + 1000}
    >
      <group position={[0, 0, 2]}>
        <Box name="passage-white-cover" args={[passageCoverSize.width, passageCoverSize.depth, 0.004]} renderOrder={RENDER_ORDERS.passage + 1000}>
          <meshBasicMaterial attach="material" color="white" transparent={false} opacity={1} depthTest={false} depthWrite={false} />
        </Box>

        <Box
          name="passage-box"
          args={[passageWidth, wallModel.depth, 0.006]}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            handlePointerEvents(e, onClick);
          }}
          onPointerEnter={(e) => handlePointerEvents(e, () => setHovered(true))}
          onPointerLeave={(e) => handlePointerEvents(e, () => setHovered(false))}
          renderOrder={RENDER_ORDERS.passage + 1001}
        >
          <meshBasicMaterial attach="material" color={meshColor} transparent={false} opacity={1} depthTest={false} depthWrite={false} />
        </Box>

        {passageDashSegments.map((dash, idx) => (
          <Box key={`passage-dash-${idx}`} args={dash.size} position={dash.position} rotation={[0, 0, dash.rotation]} renderOrder={RENDER_ORDERS.passage + 1100}>
            <meshBasicMaterial attach="material" color="black" transparent={false} opacity={1} depthTest={false} depthWrite={false} />
          </Box>
        ))}

        {(hovered || isSelected || showPassageLength) && <PassageLengthIndicator wallSideALength={passageWidth / UNIT_TO_METER_DIVISOR} rotation={angle} />}
      </group>
    </group>
  );
};

export default memo(Passage);
